import { UserPreferenceKeys } from '@common/preferences/types';
import { UserPreferenceProvider, useUserPreferenceStore } from '@common/preferences/userPreference';
import { ProphecyLoader } from '@common/ProphecyLoader';
import { QueryKeys } from '@common/queries/common';
import { INVITED_TEAM_ID_QUERY_PARAM } from '@common/url/constants';
import { isProductTypeMatching, replaceProductType } from '@common/urlBasePath';
import { useUserTeamsFromUid } from '@common/useUserTeamsFromUid';
import { Product, ProductType } from '@prophecy/graphql-types/enums';
import { removeQueryParam } from '@prophecy/utils/history';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { parseJSON } from '@prophecy/utils/string';
import { useQuery } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { migrateEntityFabric } from '../data/apis/api';
import { toFabricObject } from '../data/apis/fabricUtils';
import { errorHandler, makeGraphQlCall, useRestQuery } from '../data/util';
import { useGlobalVariables } from './globalConfig/securedConfigHook';
import { useMixPanel } from './mixpanel/context';
import { getUserQuery, UserQueryResponse, UserFabric, User } from './query';
import { UserFabricStatus, UserFabricStatusKnownStatus } from './types';
import { UserSessionContext } from './UserSessionProvider';

export type UserProviderType = {
  user: ReturnType<typeof toUserObject>;
  userCurrentTeamProductType: Product | undefined;
  refetch: ReturnType<typeof useQuery>['refetch'];
};

export const UserContext = createContext<UserProviderType | undefined>(undefined);

export function useUser() {
  return useContext(UserContext) as UserProviderType;
}

export const isProphecyUser = (email: string) => {
  return email.endsWith('@prophecy.io');
};

/**
 * This function is used to resolve the product type of a team.
 * @param team - The team to resolve the product type for.
 * @param isVerticalAppsEnabled - Whether vertical apps are enabled.
 * @returns The product type of the team.
 *
 * If the team has no product type, it will return the default product type for vertical apps (Professional).
 */
export function resolveTeamProductType(
  team: { productType?: ProductType | null } | undefined,
  isVerticalAppsEnabled: boolean
): ProductType | undefined {
  if (!team) return undefined;
  return team.productType ?? (isVerticalAppsEnabled ? Product.Professional : undefined);
}

async function user(userUID: string, isSqlProject: boolean, hasAirflow: boolean) {
  return await fetchUserFabricDocu(userUID, isSqlProject, hasAirflow);
}

/**
 * Hook to get the user session ID from UserSessionContext.
 * @requires UserSessionProvider to be wrapped around the UserProvider
 * @returns The user's session ID
 */
export const useUserSessionId = () => {
  const sessionId = useContext(UserSessionContext);

  if (!sessionId) {
    // This will happen if the UserSessionProvider is not wrapped around the UserProvider
    return 'NO_SESSION_ID';
  }
  return sessionId;
};

export function UserProvider({ children, userUID }: { children: React.ReactNode; userUID: string }) {
  const globalConf = useGlobalVariables();
  const executionUrl = globalConf.getExecutionURL as string;
  const [migrated, setMigrated] = useState(false);
  // UserProvider is mounted above the <Router>, so react-router hooks are unavailable here;
  // read the invite param straight from the browser URL instead.
  const invitedTeamId = new URLSearchParams(window.location.search).get(INVITED_TEAM_ID_QUERY_PARAM);

  useRestQuery(['migrateEntityFabric-user'], () => migrateEntityFabric(executionUrl), {
    onError: noop,
    onSettled: () => {
      setMigrated(true);
    },
    retry: false,
    refetchOnWindowFocus: false
  });

  const { identifyAndSetPeople } = useMixPanel();

  const {
    isInitialLoading: isLoading,
    isError,
    data,
    refetch
  } = useQuery(
    [QueryKeys.User],
    user.bind(null, userUID, globalConf.isSQLProjectEnabled, globalConf.isAirflowEnabled),
    {
      onError: errorHandler,
      enabled: migrated
    }
  );

  const { userPreferences, setPreference, isLoading: isUsePreferenceLoading } = useUserPreferenceStore();

  const { data: teams, isLoading: isTeamsLoading } = useUserTeamsFromUid(userUID);

  const { isVerticalAppsEnabled } = useGlobalVariables();

  // user invite flow in vertical apps - adopt the invited team id as the current workspace.
  const shouldAdoptInvitedTeam = Boolean(
    isVerticalAppsEnabled &&
    !isUsePreferenceLoading &&
    !userPreferences.verticalAppsCurrentWorkspaceId &&
    invitedTeamId &&
    teams?.some((team) => team._id === invitedTeamId)
  );
  const currentWorkspaceId =
    shouldAdoptInvitedTeam && invitedTeamId ? invitedTeamId : userPreferences.verticalAppsCurrentWorkspaceId;

  const updatedUserPreferences = useMemo(() => {
    return {
      ...userPreferences,
      // this is added to make sure verticalAppsCurrentWorkspaceId always points to the correct workspace id when the user is invited to a team
      // and the useEffect below is called to update the preference store.
      verticalAppsCurrentWorkspaceId: currentWorkspaceId
    };
  }, [userPreferences, currentWorkspaceId]);

  useEffect(() => {
    if (shouldAdoptInvitedTeam && invitedTeamId) {
      setPreference(UserPreferenceKeys.verticalAppsCurrentWorkspaceId, invitedTeamId, { saveInHistory: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAdoptInvitedTeam, invitedTeamId]);

  // clear only the invited team id from the url, keeping every other param (and the hash) intact
  useEffect(() => {
    if (invitedTeamId && currentWorkspaceId) {
      removeQueryParam(INVITED_TEAM_ID_QUERY_PARAM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedTeamId, currentWorkspaceId]);

  const resolvedTeamProductType = resolveTeamProductType(
    teams?.find((team) => team._id === currentWorkspaceId),
    isVerticalAppsEnabled
  );

  // teams / the workspace preference can briefly resolve empty on a focus/reconnect refetch;
  // retain the last known product type so that transient doesn't flip the URL base path
  // (/finance -> /professional) and bounce the user to the vertical-app home. Adjust during
  // render (not in an effect) to avoid an extra render — React's "store info from a previous
  // render" pattern.
  const [lastKnownTeamProductType, setLastKnownTeamProductType] = useState<typeof resolvedTeamProductType>(undefined);
  if (resolvedTeamProductType && resolvedTeamProductType !== lastKnownTeamProductType) {
    setLastKnownTeamProductType(resolvedTeamProductType);
  }
  const teamProductType = resolvedTeamProductType ?? lastKnownTeamProductType;

  useEffect(() => {
    if (isVerticalAppsEnabled && teamProductType && !isProductTypeMatching(teamProductType)) {
      replaceProductType(teamProductType);
    }
  }, [isVerticalAppsEnabled, teamProductType]);

  const value = useMemo(
    () =>
      ({
        user: data,
        userCurrentTeamProductType: teamProductType,
        refetch
      }) as UserProviderType,
    [data, refetch, teamProductType]
  );

  if (isLoading || isUsePreferenceLoading || isTeamsLoading || isError || !data) {
    return <ProphecyLoader />;
  }

  if (data?.firstName) {
    const { firstName, lastName, created, company } = data;
    const obj = {
      firstName,
      lastName,
      userId: data.id,
      signup_date: new Date(created).toISOString()
    };
    identifyAndSetPeople(data.email, company ? { ...obj, company: company as string } : obj);
  }

  return (
    <UserContext.Provider value={value}>
      <StableChildren>
        <UserPreferenceProvider preferences={updatedUserPreferences} setPreference={setPreference}>
          {children}
        </UserPreferenceProvider>
      </StableChildren>
    </UserContext.Provider>
  );
}
function toUserObject(user: User) {
  const fabrics = user.fabrics.map(toFabricObject);
  const id = user._id;
  const sqlFabricCredsMap =
    parseJSON<{ fabrics: Record<string, boolean> }>(user.SqlFabricCredsInfoAspect?.[0]?.AspectValue, {
      fabrics: {}
    })?.fabrics || {};

  const sqlFabrics: (UserFabric & { provider: string })[] = [];
  const sqlConfiguredFabrics: string[] = [];
  fabrics.forEach((fabric) => {
    if (fabric.SqlProviderInfoAspect?.length) {
      const aspectValueJson = parseJSON(fabric.SqlProviderInfoAspect?.[0]?.AspectValue, {
        providerInfo: { providerType: '' }
      });
      const providerType = aspectValueJson?.providerInfo?.providerType;
      sqlFabrics.push({ ...fabric, provider: providerType });
      if (sqlFabricCredsMap[fabric.id]) {
        sqlConfiguredFabrics.push(fabric.id);
      }
    }
  });

  let sqlFabricStatus: UserFabricStatus = UserFabricStatusKnownStatus.NoFabric;
  if (sqlFabrics.length === 0) {
    sqlFabricStatus = UserFabricStatusKnownStatus.NoFabric;
  } else if (sqlConfiguredFabrics.length > 0) {
    sqlFabricStatus = UserFabricStatusKnownStatus.AlreadyHaveFabric;
  } else {
    sqlFabricStatus = Number(sqlFabrics[0]._id);
  }

  return {
    ...user,
    id,
    fabrics,
    sqlFabrics,
    sqlFabricStatus
  };
}
async function fetchUserFabricDocu(id: string, isSqlProject: boolean, hasAirflow: boolean) {
  const query = getUserQuery(isSqlProject, hasAirflow);
  const { User: user } = await (makeGraphQlCall(query, { id }) as Promise<UserQueryResponse>);
  if (user) {
    return toUserObject(user);
  }
}
